import moment from "moment";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { AiOutlineArrowDown } from "react-icons/ai";
import { Divider, message, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { errorMessage } from "../../utils";
import { Container, Content } from "./styles";
import { Page } from "../../constants/brokers";
import apiBolsa from "../../services/apiBolsa";
import b3Logo from "../../assets/b3-logo-white.svg";
import { useAuth } from "../../contexts/AuthContext";
import { useBroker } from "../../contexts/BrokerContext";
import IntegrationButton from "../../components/IntegrationComponents/IntegrationButton";

interface IntegrationProps {
  item: Page;
}

export const IntegrationBolsa: React.FC<IntegrationProps> = ({ item }) => {
  const { user } = useAuth();
  const { initIntegration, integration, handleIntegrationInit } = useBroker();
  const [authorization, setAuthorization] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const feature = item.features[0];
  const feature2 = item.features[1];
  const feature3 = item.features[2];

  useEffect(() => {
    verifyUserIsAuthorized();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integration.providerExists]);

  const integrate = (data = {}, authorized = false) => {
    if (authorized || authorization) {
      setLoading(true);
      apiBolsa
        .post(feature.apiUrl, { ...data, email: user.user.email })
        .then(() => handleIntegrationInit())
        .catch((err) => {
          if (err?.response?.data?.message?.includes("Aguarde")) {
            message.error(err?.response?.data?.message);
          } else {
            message.error(errorMessage);
          }
        })
        .finally(() => setLoading(false));
    } else {
      window.open(
        feature2.apiUrl,
        "_blank",
        "location=yes,height=570,width=520,scrollbars=yes,status=yes"
      );
    }
  };

  const verifyUserIsAuthorized = () => {
    let count = 0;

    const requestUserAuthorization = (interval?: NodeJS.Timeout) =>
      apiBolsa
        .get(feature3.apiUrl)
        .then((response) => {
          count += 1;
          if (response?.data?.isAuthorized) {
            setAuthorization(true);
            interval && message.success("Permissão concedida com sucesso!", 5);
            interval && clearInterval(interval);
            interval && integrate({}, true);
          }

          if (count > 180) {
            interval && clearInterval(interval);
          }
          return response;
        })
        .catch((err) => {
          message.error(err?.response?.data?.message);
          interval && clearInterval(interval);
        });

    requestUserAuthorization().then((response) => {
      if (!response?.data?.isAuthorized) {
        const interval = setInterval(() => {
          requestUserAuthorization(interval);
        }, 2300);
      }
    });
  };

  const integrouEsseMes = useMemo(() => {
    return moment(integration.lastUpdate || new Date(2017, 0, 0)).isAfter(
      moment().startOf("month")
    );
  }, [integration]);

  return (
    <Container>
      <Typography.Title level={1}>Faça sua integração</Typography.Title>
      <Content>
        {integration.lastUpdate && (
          <>
            <Typography.Text strong type="secondary" className="last-update">
              Última integração:{" "}
              {moment(integration.lastUpdate)
                .utc()
                .local()
                .format("DD/MM/YYYY à[s] HH:mm")}
            </Typography.Text>
          </>
        )}

        <IntegrationButton
          loading={loading}
          integrado={integrouEsseMes}
          alt="Integrar com a B3"
          logo={b3Logo}
          setIntegrationModalVisible={() => {}}
          imgHeight={true}
          handleClick={integrate}
        />
        <Typography.Text
          type="warning"
          style={{
            padding: "16px 8px",
            display: "flex",
            fontSize: 12,
            textAlign: "center",
          }}
        >
          Caso prefira inserir manualmente as suas operações, clique em
          "Próximo" (não recomendado)
        </Typography.Text>
        <Divider />
        <Button
          size="large"
          type="button"
          color="secondary"
          variant="contained"
          style={{ float: "right" }}
          disabled={loading || initIntegration}
          startIcon={<AiOutlineArrowDown size={18} />}
          onClick={() => navigate("/xpinvestimentos/bolsa-insert-manual")}
        >
          Próximo
        </Button>
      </Content>
    </Container>
  );
};
